import { BannerProps, PredefinedBanner } from './types'
import { PredefinedBanners } from './PageFlag.constants'
import { Trans, useTranslation } from 'next-i18next/pages'
import { PHONE_NOT_REACHABLE, SCHEDULED_DOWNTIME } from 'constants/constants'
import { useStoreSelector } from 'hooks/store'
import { SIMULATION_DOWN } from 'constants/envs'
import { Flag } from '@boltenergy-be/design-system'
import dayjs from 'dayjs'
import parse from 'html-react-parser'
import styles from './PageFlag.module.scss'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { SOURCE_TYPES } from '@boltenergy-be/shared'
import { useFeatureFlagVariantKeyTyped } from 'hooks/useFeatureFlagVariantKeyTyped'
import { FEATURE_FLAGS } from 'constants/tracking'
import { setPostHogPersonProperties } from 'utils/tracking'
import Link from 'components/link/Link'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { Language } from 'types/language'
import { useRouter } from 'next/router'

export const PageFlag = ({ onClick }: BannerProps) => {
  // REDUX STORE
  const { banner, bannerType, sourceType } = useStoreSelector((store) => store.marketing)

  // Router
  const { pathname } = useRouter()

  // i18n
  const { t } = useTranslation('common')
  const language = useGetLanguage()

  // PostHog
  const [hasMounted, setHasMounted] = useState(false)
  useEffect(() => setHasMounted(true), [])
  const priceBannerVariant = useFeatureFlagVariantKeyTyped(FEATURE_FLAGS.priceBanner)
  const showPriceBanner =
    hasMounted &&
    language === Language.DUTCH &&
    (priceBannerVariant === 'link' || priceBannerVariant === 'plain') &&
    pathname !== '/simulation' &&
    pathname !== '/producers/[slug]/registration'

  // Constants
  const activeScheduledDowntime = SCHEDULED_DOWNTIME.find(({ start, end }) => dayjs().isBetween(start, end))
  const showDefaultBanner = false // set to: pathname === '/' for banner on homepage
  const showCrisisBanner = false
  const showNonPriceBanner =
    showCrisisBanner || !!activeScheduledDowntime || !!banner || !!bannerType || showDefaultBanner || PHONE_NOT_REACHABLE || SIMULATION_DOWN
  const showBanner = showNonPriceBanner || showPriceBanner

  const visibleBanner = useMemo<PredefinedBanner | null>(() => {
    if (activeScheduledDowntime) return PredefinedBanners.scheduledDowntime

    if (SIMULATION_DOWN) return PredefinedBanners.simulationDown

    if (bannerType) return PredefinedBanners[bannerType]

    if (PHONE_NOT_REACHABLE) {
      return {
        type: 'attention',
        text: t('phoneNotReachable')
      }
    }

    if (banner) {
      return {
        type: sourceType === SOURCE_TYPES.push ? 'attention' : 'light',
        text: banner,
        noOnClick: sourceType !== SOURCE_TYPES.push
      }
    }

    if (showCrisisBanner) return PredefinedBanners.middleEastEnergyCrisis

    if (showDefaultBanner) {
      return {
        type: 'attention',
        text: t('defaultBanner')
      }
    }

    if (showPriceBanner) {
      return {
        type: 'accent-vibrant',
        noOnClick: true,
        className: styles['price-banner'],
        icon: 'badgeCheck',
        text: (
          <Trans
            t={t}
            i18nKey="priceBanner"
            context={priceBannerVariant}
            components={{
              VrtNews: (
                <Link
                  variant="inline"
                  as="a"
                  target="_blank"
                  href="https://www.vrt.be/vrtnws/nl/2026/10/10/stijging-tot-60-een-overzicht-van-alle-energiecontracten-in-vl/"
                />
              )
            }}
          />
        )
      }
    }

    return null
  }, [activeScheduledDowntime, banner, bannerType, showCrisisBanner, showDefaultBanner, sourceType, t, showPriceBanner, priceBannerVariant])

  // Track 'nonPriceBannerVisible' as PostHog person property for the correct A/B test cohort/target audience
  useEffect(() => {
    if (showNonPriceBanner) {
      setPostHogPersonProperties({ nonPriceBannerVisible: true })
    }
  }, [showNonPriceBanner])

  if (!showBanner) return null

  return (
    <div
      data-flag-active={showBanner}
      className={classNames({ [styles.banner]: !visibleBanner?.noOnClick })}
      onClick={!visibleBanner?.noOnClick ? onClick : undefined}
    >
      <Flag
        type={visibleBanner?.type || 'attention'}
        className={classNames(
          { [styles['single-line']]: visibleBanner?.singleLine },
          visibleBanner?.size && styles[visibleBanner.size],
          visibleBanner?.className
        )}
        icon={visibleBanner?.icon}
        title={
          visibleBanner?.titleNsAndKey
            ? t(visibleBanner.titleNsAndKey, {
                ...(activeScheduledDowntime && {
                  count: dayjs(activeScheduledDowntime.start).diff(activeScheduledDowntime.end, 'day') === 0 ? 1 : 2,
                  date: activeScheduledDowntime.start.format('DD/MM/YYYY'),
                  start: activeScheduledDowntime.start.format('DD/MM/YYYY HH:mm'),
                  end: activeScheduledDowntime.end.format('DD/MM/YYYY HH:mm')
                })
              })
            : visibleBanner?.text
        }
      >
        {!!visibleBanner?.singleLine && ' '}
        {visibleBanner?.descriptionNsAndKey ? (
          <span className={styles.description}>{parse(t(visibleBanner.descriptionNsAndKey))}</span>
        ) : undefined}
      </Flag>
    </div>
  )
}

export default PageFlag
